import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, MenuItem } from '@mui/material';
import Chip from '@mui/material/Chip';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import AppButton from 'src/components/appComponents/AppButton';
import AppLoadingAndErrorWrapper from 'src/components/appComponents/AppLoadingAndErrorWrapper';
import FormBlock from 'src/components/appComponents/FormBlock';
import FormProvider, {
  RHFAutocomplete,
  RHFMultiCheckbox,
  RHFRadioGroup,
  RHFSelect,
  RHFTextField,
} from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import {
  useGeChannelAiSuggestionByIdQuery,
  useGetAllChannelsQuery,
  useGetChannelContentQuery,
  usePublishChannelAiSuggestionMutation,
} from 'src/graphql';
import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { PublishChannelAiSuggestioSchema } from 'src/schema';
import { IPublishChromeExtensionSuggestion } from 'src/types/chromeExtension';
import { MUSIC_TYPE } from 'src/utils/appConst';

export default function PublishChannelAiSuggestionForm() {
  const router = useRouter();
  const { suggestedVideoId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<IPublishChromeExtensionSuggestion>({
    resolver: yupResolver(PublishChannelAiSuggestioSchema),
  });

  const { data: suggestionVideo, isLoading: isLoadingGetSuggestionVideo } =
    useGeChannelAiSuggestionByIdQuery({
      id: suggestedVideoId,
    });

  const { data: channels, isLoading: isLoadingChannels } = useGetAllChannelsQuery();
  const { data: channelContent, isLoading: isLoadingChannelContent } = useGetChannelContentQuery({
    _eq: methods.watch()?.channelId?.id,
  });

  const { mutate: publishVideo, isLoading: isLoadingPublishVideo } =
    usePublishChannelAiSuggestionMutation({
      onSuccess: () => {
        enqueueSnackbar({ variant: 'success', message: 'Completed Successfully' });
        router.push(paths.dashboard.channelAiSuggestions.suggestionDetails(suggestedVideoId ?? ''));
      },
    });

  const onSubmit = useCallback(async (data: IPublishChromeExtensionSuggestion) => {
    const channelRelatedContents: any = data?.contentIds?.map((item: any) => item?.id);
    publishVideo({
      publishData: {
        fileSuggestionId: suggestedVideoId,
        channelId: data?.channelId?.id,
        classification: data?.classification && data?.classification[0],
        title: data?.title,
        description: data?.description,
        musicType: data?.musicType,
        contentIds: channelRelatedContents,
      },
    });
  }, []);

  // For set default values
  useEffect(() => {
    if (!isLoadingGetSuggestionVideo && suggestionVideo?.file_suggestions_by_pk) {
      const defaultValues: any = {
        title: {
          en: suggestionVideo?.file_suggestions_by_pk?.title,
          ar: suggestionVideo?.file_suggestions_by_pk?.title,
        },
        description: {
          en: suggestionVideo?.file_suggestions_by_pk?.description,
          ar: suggestionVideo?.file_suggestions_by_pk?.description,
        },
        classification: [suggestionVideo?.file_suggestions_by_pk?.classification],
        contentIds: [],
      };
      methods.reset(defaultValues);
    }
  }, [suggestionVideo, methods]);

  return (
    <AppLoadingAndErrorWrapper
      isLoading={isLoadingGetSuggestionVideo || isLoadingChannels}
      sx={{ height: 'auto' }}
    >
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container rowSpacing={3} alignItems="center">
          <FormBlock
            title="Channel Name*"
            inputField={
              <RHFAutocomplete
                name="channelId"
                autoHighlight
                label="Select Channel Name"
                loading={isLoadingChannels}
                options={channels?.channels ?? []}
                getOptionLabel={(option: any) => option?.name?.ar}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id}>
                    {option?.name?.ar}
                  </li>
                )}
              />
            }
          />
          <FormBlock
            title="Classification*"
            inputField={
              <RHFMultiCheckbox
                row
                name="classification"
                spacing={4}
                options={[
                  { value: 'Short', label: 'Short' },
                  { value: 'Video', label: 'Video' },
                ]}
              />
            }
          />
          <FormBlock
            title="English Title**"
            inputField={<RHFTextField name="title.en" label="Enter english title" />}
          />
          <FormBlock
            title="Arabic Title*"
            inputField={<RHFTextField name="title.ar" label="Enter arabic title" />}
          />
          <FormBlock
            title="English Description*"
            inputField={
              <RHFTextField name="description.en" label="Enter english short description" />
            }
          />
          <FormBlock
            title="Arabic Description*"
            inputField={
              <RHFTextField name="description.ar" label="Enter arabic short description" />
            }
          />
          <FormBlock
            title="Related Content*"
            inputField={
              <RHFAutocomplete
                name="contentIds"
                label="Choose content"
                multiple
                disableCloseOnSelect
                disabled={!methods.watch()?.channelId?.id}
                options={channelContent?.contents ?? []}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                loading={isLoadingChannelContent}
                renderOption={(props, option: any) => (
                  <li {...props} key={option?.id}>
                    {option?.name?.en}
                  </li>
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?.id}
                      label={option?.name?.en}
                      size="small"
                      color="primary"
                      variant="filled"
                    />
                  ))
                }
              />
            }
          />

          <FormBlock
            title="Music*"
            inputField={
              <RHFRadioGroup row spacing={3} name="musicType" options={MUSIC_TYPE ?? []} />
            }
          />

          <Grid item container justifyContent="flex-end">
            <AppButton
              label="Publish"
              type="submit"
              color="primary"
              isLoading={isLoadingPublishVideo}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </AppLoadingAndErrorWrapper>
  );
}
